import { HttpStatusCode } from 'axios';
import 'flag-icons/css/flag-icons.min.css';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { REGEX } from '../../constants/regex';
import { LoadingService } from '../../helpers/loading';
import { getI18nLanguageFromLocalStorage } from '../../helpers/localStorageHelper';
import { checkUserNameExists, checkVerificationCode, findId, resetPassword, sendFindIdCode, sendResetLink } from '../../services/accountRecoveryService';
import LoadingButton from '../common/LoadingButton';
import OptionSwitcher from '../OptionSwitch';
import FindIdForm from './FindIdForm';
import InfoPanel from './InfoPanel';
import PasswordResetViaCodeForm from './PasswordResetViaCodeForm';
import ResetPasswordForm from './ResetPasswordForm';
import SuccessPanel from './SuccessPanel';

const TAB_ID = 'ID';
const TAB_PWD = 'PWD';
const COUNTDOWN_TIME = 120;
const FIND_ID_FUNCTION = 'FindId';

// vuesax/linear arrow-left (Figma 648-13213) for the "Back to sign in" footer link.
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9.57 5.93L3.5 12l6.07 6.07" stroke="#232933" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.5 12H3.67" stroke="#232933" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Footer "Back to sign in" label. Kept in the bundle (uSync-safe) so it always renders
// correctly per language; a CMS `backToSignInLabel` value still overrides when provided.
const BACK_TO_SIGN_IN = { en: 'Back to sign in', vi: 'Quay lại đăng nhập', ko: '로그인으로 돌아가기' };

export default function AccountRecoveryPage({
    accountRecoveryHeading,
    recoveryImageUrl,
    checkIconUrl,
    translations,
    loginUrl,
    resetViaCode: initialResetViaCode,
    initialTab
}) {
    const CODE_LENGTH = 6;
    const [findIdEmailErrorType, setFindIdEmailErrorType] = useState(null);
    const [findIdSuccess, setFindIdSuccess] = useState(false);
    const [selectedOption, setSelectedOption] = useState(initialTab);
    const [codeSent, setCodeSent] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [lastEmailSentTo, setLastEmailSentTo] = useState('');
    const toastRef = useRef(null);
    const [foundUserId, setFoundUserId] = useState('');
    const [resetUserIdValue, setResetUserIdValue] = useState('');
    const [resetEmailValue, setResetEmailValue] = useState('');
    const [findIdEmail, setFindIdEmail] = useState('');
    const [findIdCode, setFindIdCode] = useState('');
    const [findIdSendError, setFindIdSendError] = useState(false);
    const [findIdCodeErrorType, setFindIdCodeErrorType] = useState(null);

    const [resetUserIdError, setResetUserIdError] = useState(false);
    const [resetEmailError, setResetEmailError] = useState(false);
    const [resetUserIdInvalid, setResetUserIdInvalid] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [lastResetSentTo, setLastResetSentTo] = useState('');

    const [resetViaCode, setResetViaCode] = useState(initialResetViaCode);
    const [newPwd, setNewPwd] = useState('');
    const [confPwd, setConfPwd] = useState('');
    const [pwdErrors, setPwdErrors] = useState({ length: false, upper: false, special: false, digit: false });
    const [pwdMatchError, setPwdMatchError] = useState(false);
    const [codeResetDone, setCodeResetDone] = useState(false);
    const [resetLinkError, setResetLinkError] = useState(false);
    const currentLanguage = getI18nLanguageFromLocalStorage();

    const showBottomLoginLink =
        (selectedOption === TAB_ID && !findIdSuccess && !findIdSendError)
        || (selectedOption === TAB_ID && findIdSuccess)
        || (selectedOption === TAB_PWD && !resetViaCode && !resetSuccess)
        || (selectedOption === TAB_PWD && resetViaCode && codeResetDone);

    const t = translations;

    useEffect(() => {
        if (!countdown) return;
        const h = setInterval(() => setCountdown(c => c - 1), 1000);
        return () => clearInterval(h);
    }, [countdown]);

    const handleSendFindId = async () => {
        const email = findIdEmail.trim();

        if (!email) {
            setFindIdEmailErrorType('required');
            return;
        }
        if (!REGEX.EMAIL.test(email)) {
            setFindIdEmailErrorType('format');
            return;
        }
        setFindIdEmailErrorType(null);

        const reqId = LoadingService.generateId();
        LoadingService.increment('SEND_FIND_ID_CODE', reqId);
        try {
            await sendFindIdCode(email, currentLanguage);
            setLastEmailSentTo(email);
            setCodeSent(true);
            setCountdown(COUNTDOWN_TIME);
            setFindIdSendError(false);
        } catch (err) {
            const status = err?.response?.status;
            const isNotFound = status === HttpStatusCode.NotFound;
            const isTooSoon = status === HttpStatusCode.TooManyRequests;

            if (isNotFound) {
                setFindIdSendError(true);
                setCodeSent(false);
                setCountdown(0);
            } else if (isTooSoon) {
                const remain = err?.response?.data?.resendInSeconds ?? 0;

                if (remain > 0) {
                    setCountdown(remain);
                    setCodeSent(true);
                    setLastEmailSentTo(email);
                }
                setFindIdSendError(false);
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    detail: t.sendCodeErrorMessage,
                    life: 3000
                });
            }
        }
    };

    const handleFindIdSubmit = async () => {
        const email = findIdEmail.trim();
        if (!email) {
            setFindIdEmailErrorType('required');
            return;
        }
        if (!REGEX.EMAIL.test(email)) {
            setFindIdEmailErrorType('format');
            return;
        }
        setFindIdEmailErrorType(null);

        const code = findIdCode.trim();
        if (!code) {
            return;
        }

        if (findIdCodeErrorType === 'invalid') {
            return;
        }

        const reqId = LoadingService.generateId();
        LoadingService.increment('SUBMIT_FIND_ID', reqId);
        try {
            const { data } = await findId(email, code);
            setFoundUserId(data.userId);
            setFindIdSuccess(true);
        } catch {
            setFindIdCodeErrorType('invalid');
        } finally {
            LoadingService.decrement('SUBMIT_FIND_ID', reqId);
        }
    };

    const handleSendResetLink = async () => {
        const userId = resetUserIdValue.trim();
        const email = resetEmailValue.trim();

        const uErr = !userId;
        const eErr = !REGEX.EMAIL.test(email);
        setResetUserIdError(uErr || resetUserIdInvalid);
        setResetEmailError(eErr);
        if (uErr || eErr) return;

        if (!resetUserIdInvalid) {
            try {
                const { data } = await checkUserNameExists(userId);
                if (!data?.exists) {
                    setResetUserIdInvalid(true);
                    setResetUserIdError(true);
                    return;
                }
            } catch {
                setResetUserIdInvalid(true);
                setResetUserIdError(true);
                return;
            }
        }

        setResetLinkError(false);

        const reqId = LoadingService.generateId();
        LoadingService.increment('SEND_RESET_LINK', reqId);
        try {
            await sendResetLink(userId, email, currentLanguage);
            setLastResetSentTo(email);
            setResetSuccess(true);
            setResetLinkError(false);
        } catch {
            setResetLinkError(true);
        } finally {
            LoadingService.decrement('SEND_RESET_LINK', reqId);
        }
    };


    useEffect(() => {
        const okLength = newPwd.length >= 8;
        const okUppercase = REGEX.UPPERCASE.test(newPwd);
        const okSpecialChar = REGEX.SPECIAL_CHAR.test(newPwd);
        const okDigit = REGEX.DIGIT.test(newPwd);
        setPwdErrors({ length: okLength, upper: okUppercase, special: okSpecialChar, digit: okDigit });
        setPwdMatchError(newPwd !== confPwd && confPwd.length > 0);
    }, [newPwd, confPwd]);

    const handleCodeResetSubmit = async () => {
        const params = new URLSearchParams(window.location.search);
        const email = params.get('email');
        const code = params.get('code');
        try {
            const { data: result } = await resetPassword(email, code, newPwd);
            if (result.succeed) {
                setCodeResetDone(true);
            } else {
                toastRef.current?.show({ severity: 'error', detail: t.resetPasswordSubmitErrorMessage, life: 3000 });
            }
        } catch {
            toastRef.current?.show({ severity: 'error', detail: t.resetPasswordSubmitErrorMessage, life: 3000 });
        }
    };

    useEffect(() => {
        if (resetLinkError) setResetLinkError(false);
    }, [resetUserIdValue, resetEmailValue]);


    const handleOptionChange = (value) => {
        setSelectedOption(value);
    };

    const handleRetryFindId = () => {
        setFindIdSendError(false);
        setCodeSent(false);
        setFindIdEmailErrorType(null);
        setFindIdCodeErrorType(null);
        setCountdown(0);
        setFindIdEmail('');
        setFindIdCode('');
    };

    const handleFindIdCodeBlur = async () => {
        const email = findIdEmail.trim();
        const code = findIdCode.trim();
        if (!email || !REGEX.EMAIL.test(email) || code.length !== CODE_LENGTH) {
            setFindIdCodeErrorType(null);
            return;
        }
        try {
            const { data } = await checkVerificationCode(email, code, FIND_ID_FUNCTION);
            setFindIdCodeErrorType(data.valid ? null : 'invalid');
        } catch {
            setFindIdCodeErrorType('invalid');
        }
    };

    const handleUserNameBlur = async () => {
        const userName = resetUserIdValue.trim();
        if (!userName) {
            setResetUserIdInvalid(false);
            return;
        }
        try {
            const { data } = await checkUserNameExists(userName);
            setResetUserIdInvalid(!data.exists);
        } catch {
            setResetUserIdInvalid(false);
        }
    };

    const handleResetUserIdChange = (value) => {
        if (resetUserIdInvalid) setResetUserIdInvalid(false);
        if (resetUserIdError) setResetUserIdError(false);
        setResetUserIdValue(value);
    };

    const handleResetEmailChange = (value) => {
        if (resetEmailError) setResetEmailError(false);
        setResetEmailValue(value);
    };

    const handleFindIdCodeChange = (value) => {
        if (findIdCodeErrorType) setFindIdCodeErrorType(null);
        setFindIdCode(value);
    };

    return (
        <div className="flex account-recovery-page">
            <div
                className="account-recovery-img-wrapper"
                style={{ backgroundImage: `url(${recoveryImageUrl})` }}
            />
            <div className="flex flex-column align-items-center p-4 account-recovery-form-wrapper">
                <Toast ref={toastRef} position="bottom-right" className="keduall-toast" />

                {/* Single page title above the tabs (Figma item 01: "Find your account").
                    Uses the existing accountRecoveryHeading CMS prop (vary by culture). */}
                <h1 className="keduall-typography heading-h1-bold-700 text-default-txt my-6 text-center">
                    {accountRecoveryHeading || "Find your account"}
                </h1>

                <div className="mb-6 id-pwd-switcher">
                    <OptionSwitcher
                        options={[
                            { label: t.findIdTabLabel, value: TAB_ID },
                            { label: t.resetPwdTabLabel, value: TAB_PWD }
                        ]}
                        selected={selectedOption}
                        onSelect={handleOptionChange}
                        size="large"
                    />
                </div>

                {selectedOption === TAB_ID && !findIdSuccess && (
                    <>
                        {findIdSendError ? (
                            <div className="error-panel w-full mb-6">
                                <div className="error-header w-full flex items-center justify-center gap-2 mb-6 align-items-center">
                                    <p className="keduall-typography heading-h6-normal-400 m-0 text-center text-danger-txt">
                                        {findIdSendError ? t.findIdEmailNotFoundMessage : t.findIdErrorMessage}
                                    </p>
                                </div>
                                <div className="w-full flex gap-2 justify-center mb-3">
                                    <button
                                        onClick={handleRetryFindId}
                                        className="keduall-buttons button-outlined-default-normal-secondary keduall-typography heading-h6-semibold-600 flex-1"
                                    >
                                        {t.retryFindIdButtonLabel}
                                    </button>
                                    <a
                                        href="/register"
                                        className="keduall-buttons button-filled-default-large-primary keduall-typography heading-h6-semibold-600 flex-1 text-center text-white-txt"
                                    >
                                        {t.registerButtonLabel}
                                    </a>
                                </div>
                                <div className="w-full mb-4 text-center">
                                    <p className="keduall-typography heading-h6-normal-400 m-0 inline-block">
                                        {t.haveAccountText}{' '}
                                        <a href={loginUrl} className="text-primary-default">
                                            {t.loginButtonLabel}
                                        </a>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <FindIdForm
                                    emailValue={findIdEmail}
                                    codeValue={findIdCode}
                                    onEmailChange={(value) => {
                                        setFindIdEmail(value);
                                        setFindIdEmailErrorType(null);
                                    }}
                                    onCodeChange={handleFindIdCodeChange}
                                    onSend={handleSendFindId}
                                    onSubmit={handleFindIdSubmit}
                                    t={t}
                                    loginUrl={loginUrl}
                                    countdown={countdown}
                                    emailErrorType={findIdEmailErrorType}
                                    codeErrorType={findIdCodeErrorType}
                                    onCodeBlur={handleFindIdCodeBlur}
                                    codeSent={codeSent}
                                    emailPlaceholder={t.findIdEmailPlaceholder || 'example@dotore.com'}
                                    codeSentMessage={codeSent ? t.findIdCodeSentMessage.replace('{0}', lastEmailSentTo) : ''}
                                    codeSentEmail={lastEmailSentTo}
                                />
                            </>
                        )}
                    </>
                )}

                {selectedOption === TAB_ID && findIdSuccess && (
                    <SuccessPanel
                        userId={foundUserId}
                        userIdInputId="foundUserId"
                        iconUrl={checkIconUrl}
                        message={t.findIdSuccessMessage}
                        loginUrl={loginUrl}
                        loginButtonLabel={t.loginButtonLabel}
                        resetLabel={t.resetPasswordButtonLabel}
                        onReset={() => handleOptionChange(TAB_PWD)}
                        hideLoginButton
                    />
                )}

                {selectedOption === TAB_PWD && (
                    resetViaCode
                        ? (
                            !codeResetDone
                                ? <PasswordResetViaCodeForm
                                    password={newPwd}
                                    confirmPassword={confPwd}
                                    onPasswordChange={setNewPwd}
                                    onConfirmChange={setConfPwd}
                                    errors={pwdErrors}
                                    matchError={pwdMatchError}
                                    onSubmit={handleCodeResetSubmit}
                                    t={t}
                                />
                                : <div className="reset-success-panel p-d-flex p-flex-column p-ai-center p-gap-2 p-py-6">
                                    <div className="p-d-flex p-ai-center gap-2 p-flex-nowrap reset-success-message px-4">
                                        <img
                                            src={checkIconUrl}
                                            alt="Success"
                                            width={32}
                                            height={32}
                                            className="flex-shrink-0"
                                        />
                                        <span className="keduall-typography heading-h6-normal-400">
                                            {t.resetSuccessMessage}
                                        </span>
                                    </div>
                                </div>
                        )
                        : (
                            !resetSuccess ? (
                                <>
                                    {resetLinkError && (
                                        <div className="error-panel w-full mb-6">
                                            <div className="error-header w-full flex items-center justify-center gap-2 align-items-center">
                                                <p className="keduall-typography heading-h6-normal-400 m-0 text-center text-danger-txt">
                                                    {t.resetPasswordErrorMessage}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <ResetPasswordForm
                                        onSend={handleSendResetLink}
                                        t={t}
                                        loginUrl={loginUrl}
                                        resetUserIdValue={resetUserIdValue}
                                        resetEmailValue={resetEmailValue}
                                        onResetUserIdChange={handleResetUserIdChange}
                                        onResetEmailChange={handleResetEmailChange}
                                        resetUserIdError={resetUserIdError}
                                        resetUserIdInvalid={resetUserIdInvalid}
                                        resetEmailError={resetEmailError}
                                        onResetUserIdBlur={handleUserNameBlur}
                                    />
                                </>
                            ) : (
                                <div className="reset-link-sent-panel">
                                    {resetLinkError && (
                                        <div className="error-panel w-full mb-6">
                                            <div className="error-header w-full flex items-center justify-center gap-2 align-items-center">
                                                <p className="keduall-typography heading-h6-normal-400 m-0 text-center text-danger-txt">
                                                    {t.resetPasswordErrorMessage}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <InfoPanel
                                        message={t.resetLinkSentMessage.replace('{0}', lastResetSentTo)}
                                        boldText={lastResetSentTo}
                                        show
                                        className="mb-6"
                                    />
                                    <LoadingButton
                                        onClick={handleSendResetLink}
                                        label={t.resendLinkButtonLabel}
                                        className="reset_submit keduall-buttons button-filled-default-large-primary keduall-typography heading-h6-semibold-600 text-white-txt"
                                    />
                                </div>
                            )
                        )
                )}

                {showBottomLoginLink && (
                    <div className="ar-footer w-full">
                        <div className="ar-footer-divider" />
                        <a href={loginUrl} className="ar-back-link">
                            <ArrowLeftIcon />
                            <span>{t.backToSignInLabel || BACK_TO_SIGN_IN[currentLanguage] || BACK_TO_SIGN_IN.en}</span>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}